<template>
  <div class="unit-manager">
    <div v-if="loading" class="text-center py-3">
      <i class="fas fa-spinner fa-spin"></i> Loading units...
    </div>

    <div v-else>
      <table class="table table-sm" v-if="units.length">
        <thead>
          <tr>
            <th>Unit</th>
            <th>Base?</th>
            <th>Contains (x base unit)</th>
            <th>Selling Price</th>
            <th>Cost Price</th>
            <th>Active</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="unit in units" :key="unit.id">
            <td><input class="form-control form-control-sm" v-model="unit.unit_name" /></td>
            <td>
              <span v-if="unit.is_base_unit" class="badge badge-primary">Base</span>
              <span v-else>&mdash;</span>
            </td>
            <td>
              <input
                type="number" step="any" min="0.0001"
                class="form-control form-control-sm"
                v-model="unit.conversion_factor"
                :disabled="unit.is_base_unit"
                :title="unit.is_base_unit ? 'The base unit is always 1' : `1 ${unit.unit_name} = this many base units`"
              />
            </td>
            <td><input type="number" step="any" min="0" class="form-control form-control-sm" v-model="unit.selling_price" /></td>
            <td><input type="number" step="any" min="0" class="form-control form-control-sm" v-model="unit.cost_price" /></td>
            <td class="text-center"><b-form-checkbox v-model="unit.is_active" switch /></td>
            <td class="text-nowrap">
              <button type="button" class="btn btn-outline-primary btn-sm mr-1" :disabled="saving" @click="saveUnit(unit)">Save</button>
              <button
                type="button"
                class="btn btn-outline-danger btn-sm"
                v-if="!unit.is_base_unit"
                :disabled="saving"
                @click="removeUnit(unit)"
              >Delete</button>
            </td>
          </tr>
        </tbody>
      </table>

      <p v-else class="text-muted small">No units defined yet for this {{ ownerType }} — add its base unit first (e.g. "Piece" or "Can").</p>

      <div v-if="showAddForm" class="border rounded p-3 mt-2">
        <p v-if="isFirstUnit" class="text-muted small mb-2">
          This will be the <strong>base unit</strong> — the smallest unit stock is tracked in. Every other unit you add later (e.g. Box) is defined as a multiple of this one.
        </p>
        <div class="form-row">
          <div class="form-group" :class="isFirstUnit ? 'col-md-6' : 'col-md-4'">
            <label>{{ isFirstUnit ? 'Base Unit Name' : 'New Unit Name' }}</label>
            <input class="form-control form-control-sm" v-model="newUnit.unit_name" :placeholder="isFirstUnit ? 'e.g. Piece' : 'e.g. Box'" />
          </div>
          <div class="form-group col-md-4" v-if="!isFirstUnit">
            <label>1 {{ newUnit.unit_name || 'unit' }} = how many base units?</label>
            <input type="number" step="any" min="0.0001" class="form-control form-control-sm" v-model="newUnit.conversion_factor" placeholder="e.g. 24" />
          </div>
          <div class="form-group" :class="isFirstUnit ? 'col-md-6' : 'col-md-4'">
            <label>Selling Price (optional)</label>
            <input type="number" step="any" min="0" class="form-control form-control-sm" v-model="newUnit.selling_price" />
          </div>
        </div>
        <button type="button" class="btn btn-primary btn-sm" :disabled="saving || !newUnit.unit_name || (!isFirstUnit && !newUnit.conversion_factor)" @click="addUnit">
          {{ isFirstUnit ? 'Add Base Unit' : 'Add Unit' }}
        </button>
        <button type="button" class="btn btn-link btn-sm text-muted" @click="showAddForm = false">Cancel</button>
      </div>
      <button v-else type="button" class="btn btn-outline-secondary btn-sm mt-2" @click="showAddForm = true">
        {{ isFirstUnit ? '+ Add Base Unit' : '+ Add Another Unit' }}
      </button>
    </div>
  </div>
</template>

<script>
import axios from "axios";

/**
 * Unit CRUD UI for a Product OR a ProductVariant. Self-contained — reads
 * and writes only via ProductUnitController's routes. See
 * docs/product-variations-units-implementation-plan.md Part 16.
 */
export default {
  name: "UnitManager",
  props: {
    ownerType: { type: String, required: true, validator: v => ["product", "variant"].includes(v) },
    ownerId: { type: [String, Number], required: true },
  },
  data() {
    return {
      units: [],
      loading: false,
      saving: false,
      showAddForm: false,
      newUnit: { unit_name: "", conversion_factor: null, selling_price: null },
    };
  },
  computed: {
    // The server forces the very first unit for an owner to be the base
    // unit regardless of what's sent (see ProductUnitService::createUnit())
    // — mirror that here so the form doesn't ask for a conversion factor
    // that will just be ignored.
    isFirstUnit() {
      return this.units.length === 0;
    },
  },
  watch: {
    ownerId: {
      immediate: true,
      handler(id) {
        if (id) this.fetchUnits();
      },
    },
  },
  methods: {
    baseListUrl() {
      return this.ownerType === "variant"
        ? `/api/product-variants/${this.ownerId}/units`
        : `/api/products/${this.ownerId}/units`;
    },
    async fetchUnits() {
      this.loading = true;
      try {
        const result = await axios.get(this.baseListUrl());
        this.units = result.data;
      } catch (e) {
        this.$toasts && this.$toasts.push({ type: "error", message: "Failed to load units" });
      } finally {
        this.loading = false;
      }
    },
    async addUnit() {
      this.saving = true;
      try {
        const result = await axios.post(this.baseListUrl(), { ...this.newUnit, is_base_unit: this.isFirstUnit });
        this.units.push(result.data);
        this.newUnit = { unit_name: "", conversion_factor: null, selling_price: null };
        this.showAddForm = false;
        this.$toasts && this.$toasts.push({ type: "success", message: "Unit added" });
        this.$emit("changed");
      } catch (e) {
        const message = e.response && e.response.data ? e.response.data.message : "Failed to add unit";
        this.$toasts && this.$toasts.push({ type: "error", message });
      } finally {
        this.saving = false;
      }
    },
    async saveUnit(unit) {
      this.saving = true;
      try {
        const payload = {
          unit_name: unit.unit_name,
          conversion_factor: unit.conversion_factor,
          selling_price: unit.selling_price,
          cost_price: unit.cost_price,
          is_active: unit.is_active,
        };
        await axios.put(`/api/product-units/${unit.id}`, payload);
        this.$toasts && this.$toasts.push({ type: "success", message: "Unit updated" });
        this.$emit("changed");
      } catch (e) {
        const message = e.response && e.response.data ? e.response.data.message : "Failed to update unit";
        this.$toasts && this.$toasts.push({ type: "error", message });
      } finally {
        this.saving = false;
      }
    },
    async removeUnit(unit) {
      if (!confirm(`Delete unit "${unit.unit_name}"?`)) return;
      this.saving = true;
      try {
        await axios.delete(`/api/product-units/${unit.id}`);
        this.units = this.units.filter(u => u.id !== unit.id);
        this.$toasts && this.$toasts.push({ type: "success", message: "Unit deleted" });
        this.$emit("changed");
      } catch (e) {
        const message = e.response && e.response.data ? e.response.data.message : "Failed to delete unit";
        this.$toasts && this.$toasts.push({ type: "error", message });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
